/**
 * Phase 18U U16: charts awaiting review across patients (supervisors and QA).
 * Errors are thrown as ApiError (apiClient); show them with describeApiError.
 */
import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type { ChartReviewItem, ChartReviewPage, ChartReviewQuery } from '../../dtos/patientmr/chartReview.dto';

const PATH = '/patientmr/visits/review';
/** Safety cap on pages fetched for one list (the server already caps each page) */
export const MAX_REVIEW_PAGES = 20;

const sortOldestFirst = (items: ChartReviewItem[]): ChartReviewItem[] =>
  [...items].sort((a, b) => {
    if (!a.submittedAt && !b.submittedAt) return String(a.visitDate || '').localeCompare(String(b.visitDate || ''));
    if (!a.submittedAt) return 1;
    if (!b.submittedAt) return -1;
    return a.submittedAt.localeCompare(b.submittedAt);
  });

export class ChartReviewService {
  /** One page; pass the previous `paginationToken` with the same query to continue */
  static async page(query: ChartReviewQuery = {}, paginationToken?: string | null): Promise<ChartReviewPage> {
    const params: Record<string, string> = {};
    if (query.statuses?.length) params.statuses = query.statuses.join(',');
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.limit) params.limit = String(query.limit);
    if (paginationToken) params.paginationToken = paginationToken;
    const qs = new URLSearchParams(params).toString();
    const response: ApiResponse<ChartReviewPage> = await apiClient.get(qs ? `${PATH}?${qs}` : PATH);
    if (response.success && response.data) {
      return { items: response.data.items || [], paginationToken: response.data.paginationToken ?? null };
    }
    throw new Error(response.error?.message || 'Failed to load the charts awaiting review');
  }

  /**
   * Every chart awaiting review, oldest submission first. The server can return short (even
   * empty) pages with a token, so this pages until the token is null (contract.md, U16).
   * `truncated` is true if the page cap was reached first.
   */
  static async all(query: ChartReviewQuery = {}): Promise<{ items: ChartReviewItem[]; truncated: boolean }> {
    const items: ChartReviewItem[] = [];
    let token: string | null = null;
    for (let i = 0; i < MAX_REVIEW_PAGES; i += 1) {
      // eslint-disable-next-line no-await-in-loop -- each page needs the previous token
      const page: ChartReviewPage = await ChartReviewService.page({ limit: 100, ...query }, token);
      items.push(...page.items);
      token = page.paginationToken;
      if (!token) return { items: sortOldestFirst(items), truncated: false };
    }
    return { items: sortOldestFirst(items), truncated: true };
  }
}

/**
 * The queue a supervisor is walking (in memory only, ids only). The review layout reads it to
 * offer "Next chart" after Approve or Return.
 */
export const ReviewQueue = (() => {
  let list: { patientId: string; visitId: string }[] = [];
  return {
    set(items: { patientId: string; visitId: string }[]) { list = items.map(({ patientId, visitId }) => ({ patientId, visitId })); },
    clear() { list = []; },
    /** Position of a visit in the queue, or -1 */
    indexOf(visitId: string) { return list.findIndex((x) => x.visitId === visitId); },
    /** The entry after `visitId`, if the visit is in the queue */
    next(visitId: string) {
      const i = list.findIndex((x) => x.visitId === visitId);
      return i >= 0 && i + 1 < list.length ? list[i + 1] : null;
    },
    size() { return list.length; },
  };
})();
